import { AuthService } from '../authService';

const readJson = async <T>(response: Response): Promise<T> => {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : `Voice request mislukt (${response.status}).`);
  }
  return payload as T;
};

export class VoiceStackService {
  static async synthesizeSpeech(input: {
    text: string;
    provider?: 'auto' | 'elevenlabs' | 'supertonic' | 'voxcpm';
    voiceId?: string;
    language?: string;
    voiceStyle?: string;
    voiceDesignPrompt?: string;
    cfgValue?: number;
    inferenceTimesteps?: number;
  }, signal?: AbortSignal): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    const response = await AuthService.authFetch('/voice/stack/speech', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
      signal,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'Voice speech mislukt.');
    }

    return {
      audio: await response.arrayBuffer(),
      provider: response.headers.get('X-BDAT-Voice-Provider') || input.provider || 'auto',
      latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
    };
  }

  static async synthesizeElevenLabs(text: string, voiceId?: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    const response = await AuthService.authFetch('/voice/stack/elevenlabs/stream', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({ text, voiceId }),
      signal,
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'ElevenLabs TTS mislukt.');
    }

    return response.arrayBuffer();
  }

  static async generateTurn(input: {
    apiKey?: string;
    model: string;
    systemPrompt: string;
    prompt: string;
    signal?: AbortSignal;
  }): Promise<{ text: string; model: string }> {
    const { signal, ...payload } = input;
    const response = await AuthService.authFetch('/voice/stack/openrouter/turn', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(payload),
      signal,
    });
    return readJson<{ text: string; model: string }>(response);
  }

  static async checkDeepgram(): Promise<{ ok: boolean; degraded?: boolean; message?: string; error?: string }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/status', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson(response);
  }

  static async getDeepgramToken(): Promise<{ accessToken: string; expiresIn: number }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/token', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify({}),
    });
    return readJson(response);
  }

  static async transcribeDeepgramAudio(audio: Blob): Promise<{ transcript: string; confidence: number }> {
    const response = await AuthService.authFetch('/voice/stack/deepgram/transcribe', {
      method: 'POST',
      headers: AuthService.authHeaders(false),
      body: audio,
    });
    return readJson(response);
  }

  static async testVoxcpm(input: { text?: string; voiceDesignPrompt?: string; voiceStyle?: string } = {}): Promise<{ audio: ArrayBuffer; provider: string; latencyMs: number }> {
    const response = await AuthService.authFetch('/voice/stack/voxcpm/test', {
      method: 'POST',
      headers: AuthService.authHeaders(true),
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(payload?.error ? `${payload.error}${payload.details ? ` ${payload.details}` : ''}` : 'VoxCPM2 test mislukt.');
    }

    return {
      audio: await response.arrayBuffer(),
      provider: response.headers.get('X-BDAT-Voice-Provider') || 'voxcpm',
      latencyMs: Number(response.headers.get('X-BDAT-Voice-Latency-Ms') || '0'),
    };
  }
}
